import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';

import { BACKGROUND, GREEN, ORANGE, PEARL, PRIMARY, RED, TEXT } from '../../../shared/Helpers/colors';
import { CancellationStatus, CancellationType } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';
import { DayProperties } from '../Helpers/interfaces';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { isDateWithinCancellationRange } from '../../../redux/State/clientSlice/calendarSlice';
import { AdminTypes } from '../../../components/Menu';
import { isPastTime } from '../Helpers/functions';
import {
	setAddDiscretionary,
	setAddScheduledSession,
	setCopyEvent,
	setDeleteEvent,
	setSelectedDay,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { Text } from '../../../shared/uiComponents';
import { Copy, InfoSvg } from '../../../shared/Assets/svgIcons';
import { Add, Schedule, TrashEmpty } from '../../../shared/Assets/sessionDetailsSVGs';
import { useScreenWidth } from '../../../shared/Helpers/hooks';

const CancellationStrip = ({ color, tooltip }: { color: string; tooltip: string }) => (
	<Tooltip title={tooltip} arrow>
		<div
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				height: 15,
				backgroundColor: color,
				borderRadius: '2px',
			}}
		/>
	</Tooltip>
);

// Helper function to determine admin actions positioning based on day position
const getAdminActionsPosition = (dayIndex: number) => {
	const weekIndex = Math.floor(dayIndex / 7);
	const dayInWeek = dayIndex % 7;

	// First 3 weeks: bottom positioning, rest: top positioning
	const isBottomWeek = weekIndex < 3;

	// First 3 days of week: left side, rest: right side (reversed from original)
	const isLeftSide = dayInWeek < 3;

	return {
		positionClass: `${isBottomWeek ? 'bottom' : 'top'}-${isLeftSide ? 'left' : 'right'}`,
	};
};

const SessionHeader = ({ item, dayIndex }: { dayIndex: number; item: DayProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const today = new Date();

	const currentDay = today.getDate();
	const currentMonth = today.getMonth();
	const isCurrentMonthDay = currentMonth === item.month;
	const isToday = currentDay === item.day && isCurrentMonthDay;

	const textColor = isToday ? BACKGROUND.PRIMARY[100] : isCurrentMonthDay ? PEARL.BASE[500] : TEXT.QUATERNARY;
	const textBackgroundColor = isToday ? PRIMARY.LIGHT[32] : BACKGROUND.PRIMARY[100];

	const [openActions, setOpenActions] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const roleRestriction = useSelector((state) => state.account.roleRestriction);
	const userId = useSelector((state) => state.account.user.id);
	const filteredTherapist = useSelector((state) => state.calendarHelper.filterData.therapist);

	const providerCancellations = useSelector((state) => state.scheduleCancellation.providerCancellations);
	const pendingRequests = useSelector((state) => state.scheduleCancellation.pendingRequests);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const usage = useSelector((state) => state.userSessionAllocation.adminHoursUsage);
	const canLogDiscretionary = usage?.userId === userId && [AdminTypes.admin, AdminTypes.cs, AdminTypes.bcba, AdminTypes.bt].includes(role?.section.id ?? -1);
	const allowedAfterDate = new Date(item.year, item.month, item.day);
	allowedAfterDate.setDate(allowedAfterDate.getDate() + roleRestriction);
	const isPast = isPastTime(today, allowedAfterDate) && !isAdmin;

	const cellDate = new Date(item.year, item.month, item.day);
	const matchingCancellations = providerCancellations.filter((d) => {
		if (d.type !== CancellationType.ExtendedTimeOff) return false;
		return isDateWithinCancellationRange(cellDate, d.startDate, d.endDate);
	});
	const approvedCancellation = matchingCancellations.find((d) =>
		d.status === CancellationStatus.Applied || d.status === CancellationStatus.Approved,
	);
	const pendingCancellationFromOverlay = matchingCancellations.find((d) => d.status === CancellationStatus.Pending);

	const targetProviderId = isAdmin ? filteredTherapist?.id : userId;
	const pendingCancellationFromRequests = targetProviderId
		? pendingRequests.find((r) => {
				if (r.providerId !== targetProviderId) return false;
				return isDateWithinCancellationRange(cellDate, r.startDate, r.endDate);
		  })
		: undefined;

	const pendingCancellation = pendingCancellationFromOverlay ?? pendingCancellationFromRequests;
	const hasPendingCancellation = !!pendingCancellation && !approvedCancellation;
	const hasApprovedCancellation = !!approvedCancellation;

	const toggleAdminActionsHandler = () => setOpenActions(!openActions);

	const openDeleteEventsHandler = () => {
		dispatch(setDeleteEvent(true));
		setOpenActions(false);
	};

	const openCopyEventsHandler = () => {
		dispatch(setCopyEvent(true));
		setOpenActions(false);
	};

	const openAddSessionHandler = () => {
		dispatch(setAddScheduledSession(true));
		dispatch(setSelectedDay(item));
		setOpenActions(false);
	};

	const openDiscretionaryHandler = () => {
		dispatch(setSelectedDay(item));
		dispatch(setAddDiscretionary(true));
		setOpenActions(false);
	};

	// Get positioning information for admin actions
	const { positionClass } = getAdminActionsPosition(dayIndex);

	return (
		<div className="header" style={{ position: 'relative' }}>
			{hasPendingCancellation && (
				<CancellationStrip
					color={ORANGE.QUATERNARY}
					tooltip={`Pending: ${'reasonName' in (pendingCancellation ?? {}) ? (pendingCancellation as { reasonName: string }).reasonName : 'Cancellation'}`}
				/>
			)}
			{hasApprovedCancellation && (
				<CancellationStrip
					color={GREEN.QUATERNARY}
					tooltip={`Approved: ${approvedCancellation?.reasonName || 'Cancellation'}`}
				/>
			)}
			<Text
				title={item.day}
				fontWeight="semiBold"
				color={textColor}
				type={isMobile ? 'pxs' : 'psm'}
				style={{
					backgroundColor: textBackgroundColor,
					padding: '0 8px',
					borderRadius: '999px',
				}}
			/>
			<div className="sessionHeaderActions">
				<div className="icon" role="button" tabIndex={0} aria-label="Day actions" onClick={toggleAdminActionsHandler} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAdminActionsHandler(); } }}>
					<InfoSvg onClick={toggleAdminActionsHandler} />
					<div
						style={{ display: openActions ? 'flex' : 'none' }}
						className={`adminActions adminActions-${positionClass}`}
					>
						{!isPast && (
							<div className="actionRow" role="button" tabIndex={0} aria-label="Add session" onClick={openAddSessionHandler} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAddSessionHandler(); } }}>
								<div className="actionIcon">
									<Add />
								</div>
								<div className="actionContent">
									<Text title="Add session" type="psm" fontWeight="medium" />
								</div>
							</div>
						)}
						{canLogDiscretionary && !isPast && (
							<div className="actionRow" role="button" tabIndex={0} aria-label="Log Admin Hours" onClick={openDiscretionaryHandler} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDiscretionaryHandler(); } }}>
								<div className="actionIcon">
									<Schedule />
								</div>
								<div className="actionContent">
									<Text title="Log Admin Hours" type="psm" fontWeight="medium" />
								</div>
							</div>
						)}
						<div className="actionRow" role="button" tabIndex={0} aria-label="Copy events" onClick={openCopyEventsHandler} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCopyEventsHandler(); } }}>
							<div className="actionIcon">
								<Copy />
							</div>
							<div className="actionContent">
								<Text title="Copy event(s)" type="psm" fontWeight="medium" />
							</div>
						</div>
						<div className="actionRow" role="button" tabIndex={0} aria-label="Remove events" onClick={openDeleteEventsHandler} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDeleteEventsHandler(); } }}>
							<div className="actionIcon">
								<TrashEmpty color={RED.BASE} />
							</div>
							<div className="actionContent">
								<Text title="Remove event(s)" type="psm" fontWeight="medium" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SessionHeader;
